import { skipToken } from '@reduxjs/toolkit/query';
import { useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import Loader from 'components/Loader';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { useMemo } from 'react';
import { Navigate, useParams } from 'react-router';
import { useGetOfficeByIdQuery } from 'store/api/offices';
import { EntityType } from 'types/entity';
import { OfficeForm } from 'types/offices';
import { useClassifiers } from 'utils/useClassifiers';
import { OFFICE_FORM_CLASSIFIERS } from './form-sections/useOfficeForm';
import { officeDtoToForm } from './officeFormMapping';
import OfficePageForm from './OfficePageForm';

const ClassicOfficePageEdit = () => {
    const { id: officeId } = useParams();

    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: OFFICE_FORM_CLASSIFIERS,
    });

    const { data: office, isLoading, refetch, isError } = useGetOfficeByIdQuery(officeId ?? skipToken);

    const {
        customFields,
        initialCustomFieldsObject,
        isLoading: isLoadingCustomFieldData,
    } = useCustomFields({
        entityType: EntityType.OFFICE,
        fields: office?.customFields,
    });

    // Set initial form data
    const initialData = useMemo<OfficeForm | undefined>(() => {
        if (isLoadingCustomFieldData || !office) {
            return undefined;
        }
        return officeDtoToForm(office, initialCustomFieldsObject);
    }, [office, initialCustomFieldsObject, isLoadingCustomFieldData]);

    if (isLoading || isLoadingClassifiers || (initialData === undefined && !isError) || isLoadingCustomFieldData) {
        return <Loader />;
    }

    return (
        <OfficePageForm
            type="edit"
            initialData={initialData}
            classifiers={classifiers}
            refetch={() => {
                void refetch();
            }}
            entityCustomFields={customFields}
        />
    );
};

/** In the new design an office is edited card by card on its page, so its edit URL opens the Office card there. */
const OfficePageEdit = () => {
    const isNew = useIsNewDesign();
    const { id: officeId } = useParams();
    return isNew ? <Navigate to={`/office/${officeId}?edit=office`} replace /> : <ClassicOfficePageEdit />;
};

export default OfficePageEdit;
